import EventEmitter from 'events'
import { useSyncExternalStore } from 'react'
import { useAuthenticate } from './Authenticate'
import {
  usePostUserUserFavoriteJsonMutation,
  useGetUserUserFavoriteJsonQuery,
} from '../query/yapi/hk-web/FrontUserJson/query'
import { usePostV1PortalCollectionMutation, useGetV1PortalCollectionQuery } from 'core/query/yapi/swap/Collection/query'

/**
 * 收藏类型:现货/合约
 */
type FavoriteCoinType = 'spot' | 'contract'

/**
 * 组件Props类型定义
 */
type TFavoriteCoinPairProps = {
  /** 收藏类型 */
  type: FavoriteCoinType
  /** 现货交易对ID */
  tradeId?: number
  /** 合约代码 */
  contractCode?: string
  /** 渲染函数 */
  children: (props: { isFavorite: boolean; toggleFavorite: (e: Event) => Promise<void> }) => React.ReactNode
}

/** 全局事件中心 */
const favoriteCoinPairEvent = new EventEmitter()

/** localStorage key常量 */
const SPOT_STORAGE_KEY = 'coin_favoriteList'
const CONTRACT_STORAGE_KEY = 'contract_favoriteList'

/**
 * 订阅收藏状态变化
 * @param key 订阅key
 */
const subscribeCoinPair = (key: string) => (cb: (isFavorite: boolean) => void) => {
  favoriteCoinPairEvent.on(key, cb)
  return () => {
    favoriteCoinPairEvent.off(key, cb)
  }
}

/**
 * 获取收藏状态快照
 * @param props 组件props
 */
const getCoinPairSnapshot = (props: TFavoriteCoinPairProps) => () => {
  try {
    if (props.type === 'spot') {
      const localList = JSON.parse(localStorage.getItem(SPOT_STORAGE_KEY) || '[]')
      return localList.includes(props.tradeId)
    } else {
      const localList = JSON.parse(localStorage.getItem(CONTRACT_STORAGE_KEY) || '[]')
      return localList.findIndex((item) => item.contractCode === props.contractCode?.toLowerCase()) !== -1
    }
  } catch (e) {
    console.error(e)
    return false
  }
}

/**
 * 币对收藏组件
 * 支持现货和合约收藏功能
 * 登录状态下调用接口,未登录使用localStorage存储
 */
export default function FavoriteCoinPair(props: TFavoriteCoinPairProps) {
  const { loginData } = useAuthenticate()
  const isLogin = !!loginData

  const { mutate: mutateTradeCollect } = usePostUserUserFavoriteJsonMutation()
  const { mutate: mutateContractCollect } = usePostV1PortalCollectionMutation()

  // 获取用户现货收藏列表
  const { data: userSpotFavorites, refetch: refetchSpotFavorites } = useGetUserUserFavoriteJsonQuery(
    { operate: 0 },
    {
      enabled: isLogin && props.type === 'spot',
    }
  )

  // 获取用户合约收藏列表
  const { data: userContractFavorites, refetch: refetchContractFavorites } = useGetV1PortalCollectionQuery(
    {},
    {
      enabled: isLogin && props.type === 'contract',
    }
  )

  const key = props.type === 'spot' ? `spot_${props.tradeId}` : `contract_${props.contractCode}`

  // 根据在线和本地数据判断收藏状态
  const isFavorite = useSyncExternalStore<boolean>(
    subscribeCoinPair(key),
    () => {
      if (isLogin) {
        if (props.type === 'spot') {
          return userSpotFavorites?.data?.data?.some((item) => item === props.tradeId) || false
        } else {
          return (
            userContractFavorites?.data?.data?.some(
              (item) => item.contractCode === props.contractCode?.toLowerCase()
            ) || false
          )
        }
      }
      return getCoinPairSnapshot(props)()
    },
    () => false
  )

  const toggleFavorite = (e: Event): Promise<void> => {
    return new Promise((resolve) => {
      e.stopPropagation()
      if (props.type === 'spot') {
        const localList = JSON.parse(localStorage.getItem(SPOT_STORAGE_KEY) || '[]')

        if (!isFavorite) {
          if (isLogin) {
            mutateTradeCollect(
              { tradeId: props.tradeId, operate: 1 },
              {
                onSuccess: () => {
                  refetchSpotFavorites()
                },
              }
            )
          } else {
            localStorage.setItem(SPOT_STORAGE_KEY, JSON.stringify([...localList, props.tradeId]))
          }
        } else {
          if (isLogin) {
            mutateTradeCollect(
              { tradeId: props.tradeId, operate: -1 },
              {
                onSuccess: () => {
                  refetchSpotFavorites()
                },
              }
            )
          } else {
            localStorage.setItem(SPOT_STORAGE_KEY, JSON.stringify(localList.filter((id) => id !== props.tradeId)))
          }
        }
      } else {
        const localList = JSON.parse(localStorage.getItem(CONTRACT_STORAGE_KEY) || '[]')
        const contractCode = props.contractCode?.toLowerCase()
        if (!isFavorite) {
          if (isLogin) {
            mutateContractCollect([{ contractCode, status: 0 }], {
              onSuccess: () => {
                refetchContractFavorites()
                resolve(e)
              },
            })
          } else {
            localStorage.setItem(
              CONTRACT_STORAGE_KEY,
              JSON.stringify([...localList, { contractCode, status: 0, userId: '' }])
            )
            resolve(e)
          }
        } else {
          if (isLogin) {
            mutateContractCollect([{ contractCode, status: 1 }], {
              onSuccess: () => {
                refetchContractFavorites()
                resolve(e)
              },
            })
          } else {
            const newList = [...localList]
            const index = newList.findIndex((item) => item.contractCode === contractCode)
            if (index !== -1) {
              newList.splice(index, 1)
              localStorage.setItem(CONTRACT_STORAGE_KEY, JSON.stringify(newList))
              resolve(e)
            }
          }
        }
      }

      favoriteCoinPairEvent.emit(key, !isFavorite)
    })
  }
  return props.children({
    isFavorite,
    toggleFavorite,
  })
}
